import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import {Button} from "primereact/button";
import AdminDialog, {AdminDialogProperties, AdminDialogState} from "./AdminDialog";
import AdminDialogType from "../AdminDialogType";
import Message from "../../../shared/log/Message";
import {MessageType} from "../../../shared/log/MessageInterface";
import Administration from "../../session/Administration";
import {
	ConfigurationType,
	Server,
	ServerCheckMode,
	ServerConfigUserStorage,
	UserAzureAd,
	UserAzureAdInterface,
	UserCertificateLdap,
	UserDb,
	UserLdap,
	UserLdapCertificatesTrustMode,
	UserLdapInterface,
	UserPKCS12,
	UserServerCheck,
	UserServerCheckInterface,
	UserXml
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import UserSourceDialogFields from "../field/UserSourceDialogFields";
import {Dropdown} from "primereact/dropdown";
import {TabPanel, TabView} from "primereact/tabview";
import {InputText} from "primereact/inputtext";
import {InputSwitch} from "primereact/inputswitch";
import ValidateUserSourceDialog from "./ValidateUserSourceDialog";
import {Password} from "primereact/password";
import Translate from "../../../shared/translation/Translate";
import Helper from "../../../shared/components/Helper";
import Icon from "../../../shared/components/Icon";
import FunctionHelper from "../../../shared/helper/FunctionHelper";
import Info, {InfoType} from "../../../shared/components/Info";

export enum UserSourceTypeEnum {
	DB = "db",
	LDAP = "ldap",
	XML = "xml",
	AZUREAD = "azuread"
}

export interface UserSourceDialogState extends AdminDialogState<ServerConfigUserStorage> {
	panel: number;
	anonymous: boolean;
	validated: boolean;
	showValidateDialog: boolean;
}

export class UserSourceDialogDefinition extends AdminDialog<ServerConfigUserStorage,
	AdminDialogProperties<ServerConfigUserStorage>, UserSourceDialogState> {
	constructor(props: Readonly<AdminDialogProperties<ServerConfigUserStorage>>) {
		super(props);

		this.state = {
			...this.state,
			panel: 0,
			anonymous: true,
			validated: false,
			showValidateDialog: false
		};
	}

	public renderContents(): ReactNode {
		let parameter: ServerConfigUserStorage | undefined = this.state.parameter;
		let errors: Map<any, string> = this.state.fieldErrors;

		if (!FunctionHelper.exists(parameter)) {
			return null;
		}

		let type: UserSourceTypeEnum = UserSourceTypeEnum.XML;
		if (FunctionHelper.exists(parameter?.ldap)) {
			type = UserSourceTypeEnum.LDAP;
		} else if (FunctionHelper.exists(parameter?.db)) {
			type = UserSourceTypeEnum.DB;
		} else if (FunctionHelper.exists(parameter?.azureAd)) {
			type = UserSourceTypeEnum.AZUREAD;
		}

		return (
			<>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-grid">
						<label className="p-col-12 p-sm-4" htmlFor="control-type">
							<Translate {...this.props} data="user_source.type.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<Dropdown value={type} inputId="control-type"
									  options={[
										  {
											  label: Translate.value(
												  "user_source.type.xml", {ns: "dialog"}
											  ),
											  value: UserSourceTypeEnum.XML
										  },
										  {
											  label: Translate.value(
												  "user_source.type.ldap", {ns: "dialog"}
											  ),
											  value: UserSourceTypeEnum.LDAP
										  },
										  {
											  label: Translate.value(
												  "user_source.type.azuread", {ns: "dialog"}
											  ),
											  value: UserSourceTypeEnum.AZUREAD
										  }
									  ]}
									  onChange={(e) => this.onChange(UserSourceDialogFields.TYPE, e.value)}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid" hidden={type !== UserSourceTypeEnum.AZUREAD}>
					<div className="p-col-12 p-grid">
						<label className="p-col-12 p-sm-4" htmlFor="control-azure-client-id">
							<Translate {...this.props} data="user_source.azure.client_id" namespace="dialog"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={UserAzureAd.getClientIdDescription()}/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<InputText id="control-azure-client-id"
									   value={FunctionHelper.applyValue(
										   parameter?.azureAd?.clientId, UserAzureAd.getClientIdDefault()
									   )}
									   className={errors.has(UserSourceDialogFields.AZURE_CLIENT_ID) ? "p-invalid" : ""}
									   tooltip={errors.has(UserSourceDialogFields.AZURE_CLIENT_ID) ? Translate.value(
										   errors.get(UserSourceDialogFields.AZURE_CLIENT_ID)!, {ns: "message"}
									   ) : ""}
									   onInput={(e) => this.onChange(
										   UserSourceDialogFields.AZURE_CLIENT_ID, e.currentTarget.value
									   )}/>
							{this.renderFieldError(
								errors.has(UserSourceDialogFields.AZURE_CLIENT_ID) ? Translate.value(
									errors.get(UserSourceDialogFields.AZURE_CLIENT_ID)!, {ns: "message"}
								) : ""
							)}
						</div>
					</div>
					<div className="p-col-12 p-grid">
						<label className="p-col-12 p-sm-4" htmlFor="control-azure-authority">
							<Translate {...this.props} data="user_source.azure.authority" namespace="dialog"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={UserAzureAd.getAuthorityDescription()}/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<InputText id="control-azure-authority"
									   value={FunctionHelper.applyValue(
										   parameter?.azureAd?.authority, UserAzureAd.getAuthorityDefault()
									   )}
									   className={errors.has(UserSourceDialogFields.AZURE_AUTHORITY) ? "p-invalid" : ""}
									   tooltip={errors.has(UserSourceDialogFields.AZURE_AUTHORITY) ? Translate.value(
										   errors.get(UserSourceDialogFields.AZURE_AUTHORITY)!, {ns: "message"}
									   ) : ""}
									   onInput={(e) => this.onChange(
										   UserSourceDialogFields.AZURE_AUTHORITY, e.currentTarget.value
									   )}/>
							{this.renderFieldError(
								errors.has(UserSourceDialogFields.AZURE_AUTHORITY) ? Translate.value(
									errors.get(UserSourceDialogFields.AZURE_AUTHORITY)!, {ns: "message"}
								) : ""
							)}
						</div>
					</div>
					<div className="p-col-12 p-grid">
						<label className="p-col-12 p-sm-4" htmlFor="control-azure-scope">
							<Translate {...this.props} data="user_source.azure.scope" namespace="dialog"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={UserAzureAd.getScopeDescription()}/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<InputText id="control-azure-scope"
									   value={FunctionHelper.applyValue(
										   parameter?.azureAd?.scope, UserAzureAd.getScopeDefault()
									   )}
									   className={errors.has(UserSourceDialogFields.AZURE_SCOPE) ? "p-invalid" : ""}
									   tooltip={errors.has(UserSourceDialogFields.AZURE_SCOPE) ? Translate.value(
										   errors.get(UserSourceDialogFields.AZURE_SCOPE)!, {ns: "message"}
									   ) : ""}
									   onInput={(e) => this.onChange(
										   UserSourceDialogFields.AZURE_SCOPE, e.currentTarget.value
									   )}/>
							{this.renderFieldError(
								errors.has(UserSourceDialogFields.AZURE_SCOPE) ? Translate.value(
									errors.get(UserSourceDialogFields.AZURE_SCOPE)!, {ns: "message"}
								) : ""
							)}
						</div>
					</div>
					<div className="p-col-12 p-grid">
						<label className="p-col-12 p-sm-4" htmlFor="control-azure-user-role">
							<Translate {...this.props} data="user_source.azure.user_role" namespace="dialog"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={ServerConfigUserStorage.getRoleUserGroupDescription()}/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<InputText id="control-azure-user-role"
									   value={FunctionHelper.applyValue(
										   parameter?.roleUserGroup, ServerConfigUserStorage.getRoleUserGroupDefault()
									   )}
									   className={errors.has(UserSourceDialogFields.AZURE_USER_ROLE) ? "p-invalid" : ""}
									   tooltip={errors.has(UserSourceDialogFields.AZURE_USER_ROLE) ? Translate.value(
										   errors.get(UserSourceDialogFields.AZURE_USER_ROLE)!, {ns: "message"}
									   ) : ""}
									   onInput={(e) => this.onChange(
										   UserSourceDialogFields.AZURE_USER_ROLE, e.currentTarget.value
									   )}/>
							{this.renderFieldError(
								errors.has(UserSourceDialogFields.AZURE_USER_ROLE) ? Translate.value(
									errors.get(UserSourceDialogFields.AZURE_USER_ROLE)!, {ns: "message"}
								) : ""
							)}
						</div>
					</div>
					<div className="p-col-12 p-grid">
						<label className="p-col-12 p-sm-4" htmlFor="control-azure-admin-role">
							<Translate {...this.props} data="user_source.azure.admin_role" namespace="dialog"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={ServerConfigUserStorage.getRoleAdminGroupDescription()}/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<InputText id="control-azure-admin-role"
									   value={FunctionHelper.applyValue(
										   parameter?.roleAdminGroup, ServerConfigUserStorage.getRoleAdminGroupDefault()
									   )}
									   className={
										   errors.has(UserSourceDialogFields.AZURE_ADMIN_ROLE) ? "p-invalid" : ""
									   }
									   tooltip={errors.has(UserSourceDialogFields.AZURE_ADMIN_ROLE) ? Translate.value(
										   errors.get(UserSourceDialogFields.AZURE_ADMIN_ROLE)!, {ns: "message"}
									   ) : ""}
									   onInput={(e) => this.onChange(
										   UserSourceDialogFields.AZURE_ADMIN_ROLE, e.currentTarget.value
									   )}/>
							{this.renderFieldError(
								errors.has(UserSourceDialogFields.AZURE_ADMIN_ROLE) ? Translate.value(
									errors.get(UserSourceDialogFields.AZURE_ADMIN_ROLE)!, {ns: "message"}
								) : ""
							)}
						</div>
					</div>
				</fieldset>

				<div className="p-col-12 p-grid" hidden={type !== UserSourceTypeEnum.LDAP}>
					<Helper {...this.props} type={MessageType.WARNING}>
						<Translate {...this.props} data={"dialog.admin.user_source.info"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					<TabView activeIndex={this.state.panel} onTabChange={
						(e) => this.setState({panel: e.index})
					}>
						<TabPanel header={Translate.value(
							"user_source.ldap.connection.label", {ns: "dialog"}
						)}>
							<fieldset className="p-col-12 p-grid">
								<div className="p-col-12 p-grid">
									<label className="p-col-12 p-sm-4" htmlFor="control-ldap-url">
										<Translate {...this.props} data="user_source.ldap.connection.url.label"
												   namespace="dialog"/>
										<Info {...this.props} type={InfoType.Help} markdown={true}
											  data={UserLdap.getConnectionURLDescription()}/>
									</label>
									<div className="p-col-12 p-sm-8 p-grid">
										<InputText id="control-ldap-url"
												   value={FunctionHelper.applyValue(
													   parameter?.ldap?.connectionURL, UserLdap.getConnectionURLDefault()
												   )}
												   className={errors.has(UserSourceDialogFields.URL) ? "p-invalid" : ""}
												   tooltip={errors.has(UserSourceDialogFields.URL) ? Translate.value(
													   errors.get(UserSourceDialogFields.URL)!, {ns: "message"}
												   ) : ""}
												   placeholder={Translate.value(
													   "user_source.ldap.connection.url.placeholder",
													   {ns: "dialog"}
												   )}
												   onInput={(e) => this.onChange(
													   UserSourceDialogFields.URL, e.currentTarget.value
												   )}/>
										{this.renderFieldError(
											errors.has(UserSourceDialogFields.URL) ? Translate.value(
												errors.get(UserSourceDialogFields.URL)!, {ns: "message"}
											) : ""
										)}
									</div>
								</div>
								<div className="p-col-12 p-grid">
									<label className="p-col-12 p-sm-4" htmlFor="control-ldap-trustmode">
										<Translate {...this.props} data="user_source.ldap.connection.trustmode.label"
												   namespace="dialog"/>
										<Info {...this.props} type={InfoType.Help} markdown={true}
											  data={UserLdap.getCertificatesTrustModeDescription()}/>
									</label>
									<div className="p-col-12 p-sm-8 p-grid">
										<Dropdown inputId="control-ldap-trustmode"
												  value={FunctionHelper.applyValue(
													  parameter?.ldap?.certificatesTrustMode,
													  UserLdap.getCertificatesTrustModeDefault()
												  )}
												  options={[
													  {
														  label: Translate.value(
															  "user_source.ldap.connection.trustmode.validate",
															  {ns: "dialog"}
														  ),
														  value: UserLdapCertificatesTrustMode.Validate
													  },
													  {
														  label: Translate.value(
															  "user_source.ldap.connection.trustmode.acceptAll",
															  {ns: "dialog"}
														  ),
														  value: UserLdapCertificatesTrustMode.AcceptAll
													  }
												  ]}
												  onChange={(e) => this.onChange(
													  UserSourceDialogFields.TRUSTMODE, e.value
												  )}
												  appendTo={document.getElementById("root")}/>
									</div>
								</div>
								<div className="p-col-12 p-grid">
									<label className="p-col-12 p-sm-4" htmlFor="control-ldap-follow-referral">
										<Translate {...this.props} data="user_source.ldap.connection.follow_referral"
												   namespace="dialog"/>
										<Info {...this.props} type={InfoType.Help} markdown={true}
											  data={UserLdap.getFollowReferralDescription()}/>
									</label>
									<div className="p-col-12 p-sm-8 switch-container">
										<InputSwitch inputId="control-ldap-follow-referral"
													 checked={FunctionHelper.applyValue(
														 parameter?.ldap?.followReferral,
														 UserLdap.getFollowReferralDefault()
													 )}
													 onChange={(e) => this.onChange(
														 UserSourceDialogFields.FOLLOW_REFERRAL, e.value
													 )}/>
									</div>
								</div>
								<div className="p-col-12 p-grid">
									<label className="p-col-12 p-sm-4" htmlFor="control-ldap-anonymous">
										<Translate {...this.props} data="user_source.ldap.connection.anonymous.label"
												   namespace="dialog"/>
										<Info {...this.props} type={InfoType.Help} data="user_source.ldap.connection.anonymous.info"
											  options={{ns: "dialog"}}/>
									</label>
									<div className="p-col-12 p-sm-8 switch-container">
										<InputSwitch inputId="control-ldap-anonymous" checked={this.state.anonymous}
													 onChange={(e) => this.onChange(
														 UserSourceDialogFields.ANONYMOUS, e.value
													 )}/>
									</div>
								</div>
								<div className="p-col-12 p-grid" hidden={this.state.anonymous}>
									<label className="p-col-12 p-sm-4" htmlFor="control-ldap-user">
										<Translate {...this.props} data="user_source.ldap.connection.user.label"
												   namespace="dialog"/>
										<Info {...this.props} type={InfoType.Help} markdown={true}
											  data={UserLdap.getConnectionNameDescription()}/>
									</label>
									<div className="p-col-12 p-sm-8 p-grid">
										<InputText id="control-ldap-user"
												   value={FunctionHelper.applyValue(
													   parameter?.ldap?.connectionName,
													   UserLdap.getConnectionNameDefault()
												   )}
												   className={
													   errors.has(UserSourceDialogFields.USER) ? "p-invalid" : ""
												   }
												   tooltip={errors.has(UserSourceDialogFields.USER) ? Translate.value(
													   errors.get(UserSourceDialogFields.USER)!, {ns: "message"}
												   ) : ""}
												   placeholder={Translate.value(
													   "user_source.ldap.connection.user.placeholder",
													   {ns: "dialog"}
												   )}
												   onInput={(e) => this.onChange(
													   UserSourceDialogFields.USER, e.currentTarget.value
												   )}/>
										{this.renderFieldError(
											errors.has(UserSourceDialogFields.USER) ? Translate.value(
												errors.get(UserSourceDialogFields.USER)!, {ns: "message"}
											) : ""
										)}
									</div>
								</div>
								<div className="p-col-12 p-grid" hidden={this.state.anonymous}>
									<label className="p-col-12 p-sm-4" htmlFor="control-ldap-password">
										<Translate {...this.props} data="user_source.ldap.connection.password"
												   namespace="dialog"/>
										<Info {...this.props} type={InfoType.Help} markdown={true}
											  data={UserLdap.getConnectionPasswordDescription()}/>
									</label>
									<div className="p-col-12 p-sm-8 p-grid">
										<Password id="control-ldap-password" feedback={false} toggleMask={true}
												  value={FunctionHelper.applyValue(
													  parameter?.ldap?.connectionPassword,
													  UserLdap.getConnectionPasswordDefault()
												  )}
												  className={
													  errors.has(UserSourceDialogFields.PASSWORD) ? "p-invalid" : ""
												  }
												  tooltip={errors.has(UserSourceDialogFields.PASSWORD) ?
													  Translate.value(
														  errors.get(UserSourceDialogFields.PASSWORD)!,
														  {ns: "message"}
													  ) : ""}
												  onInput={(e) => this.onChange(
													  UserSourceDialogFields.PASSWORD, e.currentTarget.value
												  )}/>
										{this.renderFieldError(
											errors.has(UserSourceDialogFields.PASSWORD) ? Translate.value(
												errors.get(UserSourceDialogFields.PASSWORD)!, {ns: "message"}
											) : ""
										)}
									</div>
								</div>
							</fieldset>
						</TabPanel>
						<TabPanel header={Translate.value("user_source.ldap.user.label", {ns: "dialog"})}>
							<fieldset className="p-col-12 p-grid">
								<div className="p-col-12 p-grid">
									<label className="p-col-12 p-sm-4" htmlFor="control-ldap-user-base">
										<Translate {...this.props} data="user_source.ldap.user.base.label"
												   namespace="dialog"/>
										<Info {...this.props} type={InfoType.Help} markdown={true}
											  data={UserLdap.getUserBaseDescription()}/>
									</label>
									<div className="p-col-12 p-sm-8 p-grid">
										<InputText id="control-ldap-user-base"
												   value={FunctionHelper.applyValue(
													   parameter?.ldap?.userBase, UserLdap.getUserBaseDefault()
												   )}
												   className={
													   errors.has(UserSourceDialogFields.USER_BASE) ? "p-invalid" : ""
												   }
												   tooltip={errors.has(UserSourceDialogFields.USER_BASE) ?
													   Translate.value(
														   errors.get(UserSourceDialogFields.USER_BASE)!,
														   {ns: "message"}
													   ) : ""}
												   placeholder={Translate.value(
													   "user_source.ldap.user.base.placeholder",
													   {ns: "dialog"}
												   )}
												   onInput={(e) => this.onChange(
													   UserSourceDialogFields.USER_BASE, e.currentTarget.value
												   )}/>
										{this.renderFieldError(
											errors.has(UserSourceDialogFields.USER_BASE) ? Translate.value(
												errors.get(UserSourceDialogFields.USER_BASE)!, {ns: "message"}
											) : ""
										)}
									</div>
								</div>
								<div className="p-col-12 p-grid">
									<label className="p-col-12 p-sm-4" htmlFor="control-ldap-user-search">
										<Translate {...this.props} data="user_source.ldap.user.search.label"
												   namespace="dialog"/>
										<Info {...this.props} type={InfoType.Help} markdown={true}
											  data={UserLdap.getUserSearchDescription()}/>
									</label>
									<div className="p-col-12 p-sm-8 p-grid">
										<InputText id="control-ldap-user-search"
												   value={FunctionHelper.applyValue(
													   parameter?.ldap?.userSearch, UserLdap.getUserSearchDefault()
												   )}
												   className={
													   errors.has(UserSourceDialogFields.USER_SEARCH) ? "p-invalid" : ""
												   }
												   tooltip={errors.has(UserSourceDialogFields.USER_SEARCH) ?
													   Translate.value(
														   errors.get(UserSourceDialogFields.USER_SEARCH)!,
														   {ns: "message"}
													   ) : ""}
												   placeholder={Translate.value(
													   "user_source.ldap.user.search.placeholder",
													   {ns: "dialog"}
												   )}
												   onInput={(e) => this.onChange(
													   UserSourceDialogFields.USER_SEARCH, e.currentTarget.value
												   )}/>
										{this.renderFieldError(
											errors.has(UserSourceDialogFields.USER_SEARCH) ? Translate.value(
												errors.get(UserSourceDialogFields.USER_SEARCH)!, {ns: "message"}
											) : ""
										)}
									</div>
								</div>
							</fieldset>
						</TabPanel>
						<TabPanel header={Translate.value("user_source.ldap.groups.label", {ns: "dialog"})}>
							<fieldset className="p-col-12 p-grid">
								<div className="p-col-12 p-grid">
									<label className="p-col-12 p-sm-4" htmlFor="control-ldap-groups-base">
										<Translate {...this.props} data="user_source.ldap.groups.base.label"
												   namespace="dialog"/>
										<Info {...this.props} type={InfoType.Help} markdown={true}
											  data={UserLdap.getRoleBaseDescription()}/>
									</label>
									<div className="p-col-12 p-sm-8 p-grid">
										<InputText id="control-ldap-groups-base"
												   value={FunctionHelper.applyValue(
													   parameter?.ldap?.roleBase, UserLdap.getRoleBaseDefault()
												   )}
												   className={
													   errors.has(UserSourceDialogFields.GROUPS_BASE) ? "p-invalid" : ""
												   }
												   tooltip={errors.has(UserSourceDialogFields.GROUPS_BASE) ?
													   Translate.value(
														   errors.get(UserSourceDialogFields.GROUPS_BASE)!,
														   {ns: "message"}
													   ) : ""}
												   placeholder={Translate.value(
													   "user_source.ldap.groups.base.placeholder",
													   {ns: "dialog"}
												   )}
												   onInput={(e) => this.onChange(
													   UserSourceDialogFields.GROUPS_BASE, e.currentTarget.value
												   )}/>
										{this.renderFieldError(
											errors.has(UserSourceDialogFields.GROUPS_BASE) ? Translate.value(
												errors.get(UserSourceDialogFields.GROUPS_BASE)!, {ns: "message"}
											) : ""
										)}
									</div>
								</div>
								<div className="p-col-12 p-grid">
									<label className="p-col-12 p-sm-4" htmlFor="control-ldap-groups-search">
										<Translate {...this.props} data="user_source.ldap.groups.search.label"
												   namespace="dialog"/>
										<Info {...this.props} type={InfoType.Help} markdown={true}
											  data={UserLdap.getRoleSearchDescription()}/>
									</label>
									<div className="p-col-12 p-sm-8 p-grid">
										<InputText id="control-ldap-groups-search"
												   value={FunctionHelper.applyValue(
													   parameter?.ldap?.roleSearch, UserLdap.getRoleSearchDefault()
												   )}
												   className={
													   errors.has(UserSourceDialogFields.GROUPS_SEARCH) ?
														   "p-invalid" : ""
												   }
												   tooltip={errors.has(UserSourceDialogFields.GROUPS_SEARCH) ?
													   Translate.value(
														   errors.get(UserSourceDialogFields.GROUPS_SEARCH)!,
														   {ns: "message"}
													   ) : ""
												   }
												   placeholder={Translate.value(
													   "user_source.ldap.groups.search.placeholder",
													   {ns: "dialog"}
												   )}
												   onInput={(e) => this.onChange(
													   UserSourceDialogFields.GROUPS_SEARCH, e.currentTarget.value
												   )}/>
										{this.renderFieldError(
											errors.has(UserSourceDialogFields.GROUPS_SEARCH) ? Translate.value(
												errors.get(UserSourceDialogFields.GROUPS_SEARCH)!, {ns: "message"}
											) : ""
										)}
									</div>
								</div>
								<div className="p-col-12 p-grid">
									<label className="p-col-12 p-sm-4" htmlFor="control-ldap-groups-field">
										<Translate {...this.props} data="user_source.ldap.groups.field.label"
												   namespace="dialog"/>
										<Info {...this.props} type={InfoType.Help} markdown={true}
											  data={UserLdap.getRoleFieldDescription()}/>
									</label>
									<div className="p-col-12 p-sm-8 p-grid">
										<InputText id="control-ldap-groups-field"
												   value={FunctionHelper.applyValue(
													   parameter?.ldap?.roleField, UserLdap.getRoleFieldDefault()
												   )}
												   className={
													   errors.has(UserSourceDialogFields.GROUPS_FIELD) ?
														   "p-invalid" : ""
												   }
												   tooltip={errors.has(UserSourceDialogFields.GROUPS_FIELD) ?
													   Translate.value(
														   errors.get(UserSourceDialogFields.GROUPS_FIELD)!,
														   {ns: "message"}
													   ) : ""}
												   placeholder={Translate.value(
													   "user_source.ldap.groups.field.placeholder",
													   {ns: "dialog"}
												   )}
												   onInput={(e) => this.onChange(
													   UserSourceDialogFields.GROUPS_FIELD, e.currentTarget.value
												   )}/>
										{this.renderFieldError(
											errors.has(UserSourceDialogFields.GROUPS_FIELD) ? Translate.value(
												errors.get(UserSourceDialogFields.GROUPS_FIELD)!, {ns: "message"}
											) : ""
										)}
									</div>
								</div>
								<div className="p-col-12 p-grid">
									<label className="p-col-12 p-sm-4" htmlFor="control-ldap-groups-admin">
										<Translate {...this.props} data="user_source.ldap.groups.admin.label"
												   namespace="dialog"/>
										<Info {...this.props} type={InfoType.Help} markdown={true}
											  data={ServerConfigUserStorage.getRoleAdminGroupDescription()}/>
									</label>
									<div className="p-col-12 p-sm-8 p-grid">
										<InputText id="control-ldap-groups-admin"
												   value={FunctionHelper.applyValue(
													   parameter?.roleAdminGroup,
													   ServerConfigUserStorage.getRoleAdminGroupDefault()
												   )}
												   className={
													   errors.has(UserSourceDialogFields.GROUPS_ADMIN) ?
														   "p-invalid" : ""
												   }
												   tooltip={errors.has(UserSourceDialogFields.GROUPS_ADMIN) ?
													   Translate.value(
														   errors.get(UserSourceDialogFields.GROUPS_ADMIN)!,
														   {ns: "message"}
													   ) : ""}
												   placeholder={Translate.value(
													   "user_source.ldap.groups.admin.placeholder",
													   {ns: "dialog"}
												   )}
												   onInput={(e) => this.onChange(
													   UserSourceDialogFields.GROUPS_ADMIN, e.currentTarget.value
												   )}/>
										{this.renderFieldError(
											errors.has(UserSourceDialogFields.GROUPS_ADMIN) ? Translate.value(
												errors.get(UserSourceDialogFields.GROUPS_ADMIN)!, {ns: "message"}
											) : ""
										)}
									</div>
								</div>
								<div className="p-col-12 p-grid">
									<label className="p-col-12 p-sm-4" htmlFor="control-ldap-groups-user">
										<Translate {...this.props} data="user_source.ldap.groups.user.label"
												   namespace="dialog"/>
										<Info {...this.props} type={InfoType.Help} markdown={true}
											  data={ServerConfigUserStorage.getRoleUserGroupDescription()}/>
									</label>
									<div className="p-col-12 p-sm-8 p-grid">
										<InputText id="control-ldap-groups-user"
												   value={FunctionHelper.applyValue(
													   parameter?.roleUserGroup,
													   ServerConfigUserStorage.getRoleUserGroupDefault()
												   )}
												   className={
													   errors.has(UserSourceDialogFields.GROUPS_USER) ? "p-invalid" : ""
												   }
												   tooltip={errors.has(UserSourceDialogFields.GROUPS_USER) ?
													   Translate.value(
														   errors.get(UserSourceDialogFields.GROUPS_USER)!,
														   {ns: "message"}
													   ) : ""}
												   placeholder={Translate.value(
													   "user_source.ldap.groups.user.placeholder",
													   {ns: "dialog"}
												   )}
												   onInput={(e) => this.onChange(
													   UserSourceDialogFields.GROUPS_USER, e.currentTarget.value
												   )}/>
										{this.renderFieldError(
											errors.has(UserSourceDialogFields.GROUPS_USER) ? Translate.value(
												errors.get(UserSourceDialogFields.GROUPS_USER)!, {ns: "message"}
											) : ""
										)}
									</div>
								</div>
							</fieldset>
						</TabPanel>
						<TabPanel header={
							Translate.value("user_source.ldap.attribute.label", {ns: "dialog"})
						}>
							<fieldset className="p-col-12 p-grid">
								<div className="p-col-12 p-grid">
									<label className="p-col-12 p-sm-4" htmlFor="control-ldap-attribute-certificate">
										<Translate {...this.props} data="user_source.ldap.attribute.certificate.label"
												   namespace="dialog"/>
										<Info {...this.props} type={InfoType.Help} markdown={true}
											  data={UserCertificateLdap.getAttributeNameDescription()}/>
									</label>
									<div className="p-col-12 p-sm-8 p-grid">
										<InputText id="control-ldap-attribute-certificate"
												   value={FunctionHelper.applyValue(
													   parameter?.ldap?.userCertificates?.attributeName,
													   UserCertificateLdap.getAttributeNameDefault()
												   )}
												   className={errors.has(UserSourceDialogFields.ATTRIBUTE_CERTIFICATE) ?
													   "p-invalid" : ""
												   }
												   tooltip={errors.has(UserSourceDialogFields.ATTRIBUTE_CERTIFICATE) ?
													   Translate.value(
														   errors.get(UserSourceDialogFields.ATTRIBUTE_CERTIFICATE)!,
														   {ns: "message"}
													   ) : ""
												   }
												   placeholder={Translate.value(
													   "user_source.ldap.attribute.certificate.placeholder",
													   {ns: "dialog"}
												   )}
												   onInput={(e) => this.onChange(
													   UserSourceDialogFields.ATTRIBUTE_CERTIFICATE,
													   e.currentTarget.value
												   )}/>
										{this.renderFieldError(
											errors.has(UserSourceDialogFields.ATTRIBUTE_CERTIFICATE) ? Translate.value(
												errors.get(UserSourceDialogFields.ATTRIBUTE_CERTIFICATE)!, {ns: "message"}
											) : ""
										)}
									</div>
								</div>
								<div className="p-col-12 p-grid">
									<label className="p-col-12 p-sm-4" htmlFor="control-ldap-user-pkcs12-name">
										<Translate {...this.props} data="user_source.ldap.attribute.user_pkcs12.name"
												   namespace="dialog"/>
										<Info {...this.props} type={InfoType.Help} markdown={true}
											  data={UserPKCS12.getAttributeNameDescription()}/>
									</label>
									<div className="p-col-12 p-sm-8 p-grid">
										<InputText id="control-ldap-user-pkcs12-name"
												   value={FunctionHelper.applyValue(
													   parameter?.ldap?.userPKCS12?.attributeName,
													   UserPKCS12.getAttributeNameDefault()
												   )}
												   className={errors.has(UserSourceDialogFields.USER_PKCS12) ?
													   "p-invalid" : ""
												   }
												   tooltip={errors.has(UserSourceDialogFields.USER_PKCS12) ?
													   Translate.value(
														   errors.get(UserSourceDialogFields.USER_PKCS12)!,
														   {ns: "message"}
													   ) : ""
												   }
												   onInput={(e) => this.onChange(
													   UserSourceDialogFields.USER_PKCS12, e.currentTarget.value
												   )}/>
										{this.renderFieldError(
											errors.has(UserSourceDialogFields.USER_PKCS12) ? Translate.value(
												errors.get(UserSourceDialogFields.USER_PKCS12)!, {ns: "message"}
											) : ""
										)}
									</div>
								</div>
							</fieldset>
						</TabPanel>
					</TabView>
				</div>
			</>
		);
	}

	public renderDialog(): ReactNode {
		let parameter: ServerConfigUserStorage | undefined = this.state.parameter;

		let classList: Array<string> = ["user-source-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<>
				<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
						closeOnEscape={true} header={this.renderHeader()} resizable={false}
						footer={this.renderFooter()} appendTo={document.getElementById("root")}>
					<div className="p-grid">
						{this.renderError()}
						<Helper {...this.props}>
							<Translate {...this.props} data={"dialog.admin.user_source.general"} namespace={"help"}
									   markdown={true}/>
						</Helper>
						{this.renderContents()}
					</div>
				</Dialog>

				{FunctionHelper.exists(parameter?.ldap) &&
					<ValidateUserSourceDialog  {...this.props} visible={this.state.showValidateDialog}
											   hide={() => this.setState({showValidateDialog: false})}
											   onError={this.onError} userConfiguration={parameter!}
											   parameter={UserServerCheck.fromJson({
												   checkType: ServerCheckMode.User,
											   } as UserServerCheckInterface)}
											   setParentState={this.setState.bind(this)}/>
				}
			</>
		);
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-user source"}/>
					<Translate {...this.props} data="user_source.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected renderFooter(): ReactNode {
		let parameter: ServerConfigUserStorage | undefined = this.state.parameter;
		let showTestButton = FunctionHelper.exists(parameter?.ldap);

		return (
			<div className="p-col-12 p-grid">
				<div className="p-col-12 p-grid">
					<Button label={Translate.value("buttons.reset")} icon={"icon icon-rotate-270 small"}
							disabled={!this.state.hasChanges || this.state.submitted}
							className={showTestButton ? "" : "reset-button"} onClick={() => this.reset()}/>
					{showTestButton &&
						<Button label={Translate.value("buttons.test")} className="dialog-button reset-button"
								icon={"icon " + (
									this.state.validated ?
										(!FunctionHelper.exists(this.state.exception) ? " icon-check" : " icon-close") :
										" none")
								}
								onClick={() => this.setState({
									validated: false,
									showValidateDialog: true,
									exception: undefined
								})}/>
					}
					<Button label={Translate.value("buttons.save")} className="primary-button"
							disabled={!this.isValid()} onClick={() => this.submit()}
							icon={"icon" + (this.state.submitted ? " icon-spinner spin" : " icon-check")}/>
					{this.state.submitted ?
						<Button label={Translate.value("buttons.close")} icon="icon icon-close"
								onClick={() => this.hide()} className="dialog-button"/> :
						<Button label={Translate.value("buttons.cancel")} icon="icon icon-close"
								onClick={() => this.hide()} className="dialog-button"/>
					}
				</div>
			</div>
		);
	}

	protected isValid(): boolean {
		let parameter: ServerConfigUserStorage | undefined = this.state.parameter;

		if (FunctionHelper.exists(parameter?.azureAd)) {
			if (!FunctionHelper.exists(parameter?.azureAd?.clientId) || FunctionHelper.isEmpty(parameter?.azureAd!.clientId!)) {
				return false;
			}

			if (!FunctionHelper.exists(parameter?.roleUserGroup) || FunctionHelper.isEmpty(parameter?.roleUserGroup!)) {
				return false;
			}

			if (!FunctionHelper.exists(parameter?.roleAdminGroup) || FunctionHelper.isEmpty(parameter?.roleAdminGroup!)) {
				return false;
			}
		}

		return super.isValid() && this.state.hasChanges && this.state.validated;
	}

	protected onChange(field: UserSourceDialogFields, value: any): void {
		let parameter: ServerConfigUserStorage | undefined = this.state.parameter as ServerConfigUserStorage;
		let errors: Map<any, string> = this.state.fieldErrors;

		switch (field) {
			case UserSourceDialogFields.TYPE:
				parameter.xml = undefined;
				parameter.ldap = undefined;
				parameter.db = undefined;
				parameter.azureAd = undefined;

				switch (value) {
					case UserSourceTypeEnum.XML:
						parameter.xml = UserXml.fromJson({});
						this.setState({
							validated: true
						});
						break;
					case UserSourceTypeEnum.LDAP:
						parameter.ldap = UserLdap.fromJson({
							userCertificates: {}
						} as UserLdapInterface);
						this.setState({
							validated: false
						});
						break;
					case UserSourceTypeEnum.DB:
						parameter.db = UserDb.fromJson({});
						this.setState({
							validated: true
						});
						break;
					case UserSourceTypeEnum.AZUREAD:
						parameter.azureAd = UserAzureAd.fromJson({
							scope: "user.read",
							authority: "https://login.microsoftonline.com/organizations/"
						} as UserAzureAdInterface);
						this.setState({
							validated: true
						});
						break;

				}
				break;
			case UserSourceDialogFields.URL:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				parameter.ldap!.connectionURL = value;
				break;
			case UserSourceDialogFields.ANONYMOUS:
				if (value) {
					parameter.ldap!.connectionName = undefined;
					parameter.ldap!.connectionPassword = undefined;

					errors.delete(UserSourceDialogFields.USER);
					errors.delete(UserSourceDialogFields.PASSWORD);
				}

				this.setState({
					anonymous: value
				});
				break;
			case UserSourceDialogFields.USER:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				parameter.ldap!.connectionName = value;
				break;
			case UserSourceDialogFields.PASSWORD:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				parameter.ldap!.connectionPassword = value;
				break;
			case UserSourceDialogFields.USER_BASE:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				parameter.ldap!.userBase = value;
				break;
			case UserSourceDialogFields.USER_SEARCH:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				parameter.ldap!.userSearch = value;
				break;
			case UserSourceDialogFields.GROUPS_BASE:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				parameter.ldap!.roleBase = value;
				break;
			case UserSourceDialogFields.GROUPS_SEARCH:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				parameter.ldap!.roleSearch = value;
				break;
			case UserSourceDialogFields.GROUPS_FIELD:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				parameter.ldap!.roleField = value;
				break;
			case UserSourceDialogFields.GROUPS_USER:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				parameter.roleUserGroup = value;
				break;
			case UserSourceDialogFields.GROUPS_ADMIN:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				parameter.roleAdminGroup = value;
				break;
			case UserSourceDialogFields.ATTRIBUTE_CERTIFICATE:
				if (!FunctionHelper.exists(parameter.ldap?.userCertificates)) {
					parameter.ldap!.userCertificates = new UserCertificateLdap({});
				}

				parameter.ldap!.userCertificates!.enabled = !FunctionHelper.exists(value) || !(FunctionHelper.isEmpty(value));
				parameter.ldap!.userCertificates!.attributeName = value;
				break;
			case UserSourceDialogFields.AZURE_CLIENT_ID:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				parameter.azureAd!.clientId = value;
				break;
			case UserSourceDialogFields.AZURE_USER_ROLE:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				parameter.roleUserGroup = value;
				break;
			case UserSourceDialogFields.AZURE_ADMIN_ROLE:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				parameter.roleAdminGroup = value;
				break;
			case UserSourceDialogFields.AZURE_AUTHORITY:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				parameter.azureAd!.authority = value;
				break;
			case UserSourceDialogFields.AZURE_SCOPE:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				parameter.azureAd!.scope = value;
				break;
			case UserSourceDialogFields.USER_PKCS12:
				if (!FunctionHelper.exists(parameter.ldap?.userPKCS12)) {
					parameter.ldap!.userPKCS12 = new UserPKCS12({});
				}

				parameter.ldap!.userPKCS12!.enabled = !FunctionHelper.exists(value) || !(FunctionHelper.isEmpty(value));
				parameter.ldap!.userPKCS12!.attributeName = value;
				break;
			case UserSourceDialogFields.FOLLOW_REFERRAL:
				parameter.ldap!.followReferral = value;
				break;
			case UserSourceDialogFields.TRUSTMODE:
				parameter.ldap!.certificatesTrustMode = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		}, () => {
			super.onChange(field, value);
		});
	}

	protected async onShow(): Promise<void> {
		await super.onShow();

		let parameter: ServerConfigUserStorage | undefined = this.state.parameter;

		this.setState({
			anonymous: FunctionHelper.applyValue(
				parameter?.ldap?.connectionName, UserLdap.getConnectionNameDefault()
			) === ""
		});
	}

	protected async handleKeydown(event: KeyboardEvent): Promise<void> {
		if (this.state.showValidateDialog) {
			return;
		}

		await super.handleKeydown(event);
	}

	protected async submit(): Promise<void> {
		await super.submit();

		let administration: Administration = this.props.context.session.getAdministration();
		let serverConfiguration: Server =
			(await administration.getConfiguration(ConfigurationType.Server) as Server).clone();
		serverConfiguration.user = this.state.parameter!;

		try {
			await administration.updateConfiguration(ConfigurationType.Server, serverConfiguration);

			this.setState({
				hasChanges: false
			});
		} catch (exception: any) {
			if (await this.onError(exception)) {
				await this.submit();
			}
			return;
		}

		let message: Message = new Message("success.administration.user_source", MessageType.SUCCESS);
		this.props.context.getPage().addMessage(message);
		this.props.context.getPage().setState({
			showRestartMessage: true
		});
		this.hide();
	}

	protected async reset(): Promise<void> {
		await super.reset();

		let parameter: ServerConfigUserStorage | undefined = this.state.parameter;

		this.setState({
			validated: FunctionHelper.exists(parameter?.ldap)
		});
	}

	protected getDialogType(): AdminDialogType {
		return AdminDialogType.USER_SOURCE;
	}
}

const UserSourceDialog = React.memo(UserSourceDialogDefinition,
	(
		prevProps: Readonly<AdminDialogProperties<ServerConfigUserStorage>>,
		nextProps: Readonly<AdminDialogProperties<ServerConfigUserStorage>>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default UserSourceDialog;